import React, { useLayoutEffect, useState } from 'react';
import './PhoneFrame.css';

// An iPhone 17 is 402×874 points. A screen inside PhoneFrame is laid out at
// that size, with the Kabilah app's own measurements, then the whole phone is
// scaled to fit the space it's given.
const SCREEN_W = 402;
const SCREEN_H = 874;
const BEZEL = 14;
const PHONE_W = SCREEN_W + 2 * BEZEL;
const PHONE_H = SCREEN_H + 2 * BEZEL;
const CAPTION_SPACE = 64; // room under the phone for its caption

// The phone's scale: as large as fits, never above 1. A callback ref, because
// the stage may only appear after data loads, and it's re-measured whenever
// that space changes size.
function usePhoneScale(hasCaption) {
  const [stage, setStage] = useState(null);
  const [scale, setScale] = useState(0.7);
  useLayoutEffect(() => {
    if (!stage) return undefined;
    const reserve = hasCaption ? CAPTION_SPACE : 0;
    const fit = () => {
      const { width, height } = stage.getBoundingClientRect();
      if (width > 0 && height > reserve) setScale(Math.min(1, width / PHONE_W, (height - reserve) / PHONE_H));
    };
    fit();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(fit);
    observer.observe(stage);
    return () => observer.disconnect();
  }, [stage, hasCaption]);
  return [setStage, scale];
}

// Cellular dots, Wi-Fi and battery, as on an iPhone status bar. Decorative only.
const PhoneStatusIcons = () => (
  <span className="phone-icons">
    <svg width="22" height="6" viewBox="0 0 22 6" fill="currentColor" opacity="0.3">
      <circle cx="3" cy="3" r="2.4" /><circle cx="8.3" cy="3" r="2.4" /><circle cx="13.6" cy="3" r="2.4" /><circle cx="18.9" cy="3" r="2.4" />
    </svg>
    <svg width="18" height="13" viewBox="0 0 17 12" fill="currentColor">
      <path d="M8.5 2.3c2.4 0 4.6.9 6.3 2.5l1.2-1.3A10.6 10.6 0 0 0 8.5.5 10.6 10.6 0 0 0 1 3.5l1.2 1.3a8.8 8.8 0 0 1 6.3-2.5z" />
      <path d="M8.5 5.8c1.4 0 2.7.5 3.7 1.4l1.2-1.3a7.2 7.2 0 0 0-9.8 0l1.2 1.3c1-.9 2.3-1.4 3.7-1.4z" />
      <path d="M8.5 9.2c.5 0 1 .2 1.3.5L8.5 11.2 7.2 9.7c.3-.3.8-.5 1.3-.5z" />
    </svg>
    <svg width="28" height="13" viewBox="0 0 27 13" fill="none">
      <rect x="0.5" y="0.5" width="23" height="12" rx="3.8" stroke="currentColor" opacity="0.35" />
      <rect x="2" y="2" width="20" height="9" rx="2.5" fill="currentColor" />
      <path d="M25 4.5v4c.8-.3 1.3-1.1 1.3-2s-.5-1.7-1.3-2z" fill="currentColor" opacity="0.4" />
    </svg>
  </span>
);

// An iPhone drawn like the iOS Simulator, showing one Kabilah app screen
// (children, below the status bar), with an optional caption underneath.
// Used for the report sheet and for a nurse's home screen, so they match.
const PhoneFrame = ({ label, caption, children }) => {
  const [stageRef, scale] = usePhoneScale(Boolean(caption));
  return (
    <div className="phone-stage" ref={stageRef}>
      <div className="phone-scaler" style={{ width: PHONE_W * scale, height: PHONE_H * scale }}>
        <div
          className="phone"
          style={{ width: PHONE_W, height: PHONE_H, padding: BEZEL, transform: `scale(${scale})` }}
          aria-label={label}
        >
          <div className="phone-screen">
            <div className="phone-status" aria-hidden="true">
              <span className="phone-island" />
              <PhoneStatusIcons />
            </div>
            {children}
          </div>
        </div>
      </div>
      {caption && <p className="phone-caption">{caption}</p>}
    </div>
  );
};

export default PhoneFrame;
